import {
	Command,
	createElementSelectionResult,
	type CommandResult,
} from "@/commands/base-command";
import type { SceneTracks, TimelineElement } from "@/timeline";
import { isRetimableElement } from "@/timeline";
import { generateUUID } from "@/utils/id";
import { EditorCore } from "@/core";
import { splitAnimationsAtTime } from "@/animation";
import { getSourceSpanAtClipTime } from "@/retime";
import {
	addMediaTime,
	type MediaTime,
	roundMediaTime,
	subMediaTime,
	ZERO_MEDIA_TIME,
} from "@/wasm";

/**
 * Freeze frame (B3).
 *
 * Splits the target video clip at `freezeTime` and inserts a held still of the
 * source frame at that point, holding it for `freezeDuration`. The right half
 * and every later element on the same track ripple right by `freezeDuration`,
 * so nothing is overwritten and the clip resumes seamlessly after the freeze.
 *
 * The inserted clip carries `freeze: true`; the renderer then samples the source
 * at its `trimStart` for the whole duration instead of advancing with clip time
 * (see services/renderer/resolve.ts). Source-span math mirrors
 * SplitElementsCommand so the held frame is exactly the right half's first frame.
 */
export class FreezeFrameCommand extends Command {
	private savedState: SceneTracks | null = null;
	private freezeElement: { trackId: string; elementId: string } | null = null;
	private readonly target: { trackId: string; elementId: string };
	private readonly freezeTime: MediaTime;
	private readonly freezeDuration: MediaTime;

	constructor({
		element,
		freezeTime,
		freezeDuration,
	}: {
		element: { trackId: string; elementId: string };
		freezeTime: MediaTime;
		freezeDuration: MediaTime;
	}) {
		super();
		this.target = element;
		this.freezeTime = freezeTime;
		this.freezeDuration = freezeDuration;
	}

	getFreezeElement(): { trackId: string; elementId: string } | null {
		return this.freezeElement;
	}

	execute(): CommandResult | undefined {
		const editor = EditorCore.getInstance();
		this.savedState = editor.scenes.getActiveScene().tracks;
		this.freezeElement = null;

		const rebuildTrack = <
			TTrack extends { id: string; elements: TimelineElement[] },
		>(
			track: TTrack,
		): TTrack => {
			if (track.id !== this.target.trackId) {
				return track;
			}

			const elements = track.elements.flatMap((element): TimelineElement[] => {
				// Ripple later elements right to make room for the freeze.
				if (element.id !== this.target.elementId) {
					if (element.startTime >= this.freezeTime) {
						return [
							{
								...element,
								startTime: addMediaTime({
									a: element.startTime,
									b: this.freezeDuration,
								}),
							},
						];
					}
					return [element];
				}

				// Only video clips can be frozen, and the freeze point must fall
				// strictly inside the clip.
				if (element.type !== "video") {
					return [element];
				}
				const effectiveStart = element.startTime;
				const effectiveEnd = addMediaTime({
					a: element.startTime,
					b: element.duration,
				});
				if (
					this.freezeTime <= effectiveStart ||
					this.freezeTime >= effectiveEnd
				) {
					return [element];
				}

				const leftVisibleDuration = subMediaTime({
					a: this.freezeTime,
					b: element.startTime,
				});
				const rightVisibleDuration = subMediaTime({
					a: element.duration,
					b: leftVisibleDuration,
				});
				const retimeRef = isRetimableElement(element)
					? element.retime
					: undefined;
				// Snap the source-side split point once; derive the right span from it
				// so leftSourceSpan + rightSourceSpan == totalSourceSpan (same
				// discipline as SplitElementsCommand).
				const leftSourceSpan = roundMediaTime({
					time: getSourceSpanAtClipTime({
						clipTime: leftVisibleDuration,
						retime: retimeRef,
					}),
				});
				const totalSourceSpan = roundMediaTime({
					time: getSourceSpanAtClipTime({
						clipTime: element.duration,
						retime: retimeRef,
					}),
				});
				const rightSourceSpan = subMediaTime({
					a: totalSourceSpan,
					b: leftSourceSpan,
				});
				const leftTrimEnd = addMediaTime({
					a: element.trimEnd,
					b: rightSourceSpan,
				});
				const rightTrimStart = addMediaTime({
					a: element.trimStart,
					b: leftSourceSpan,
				});
				const { leftAnimations, rightAnimations } = splitAnimationsAtTime({
					animations: element.animations,
					splitTime: leftVisibleDuration,
					shouldIncludeSplitBoundary: true,
				});

				const freezeId = generateUUID();
				const rightId = generateUUID();
				this.freezeElement = {
					trackId: track.id,
					elementId: freezeId,
				};

				return [
					{
						...element,
						duration: leftVisibleDuration,
						trimEnd: leftTrimEnd,
						animations: leftAnimations,
					},
					{
						...element,
						id: freezeId,
						freeze: true,
						startTime: this.freezeTime,
						duration: this.freezeDuration,
						// Hold the right half's first source frame. trimEnd is ignored
						// while frozen; 0 leaves the most room for resizing the hold.
						trimStart: rightTrimStart,
						trimEnd: ZERO_MEDIA_TIME,
						retime: undefined,
						animations: undefined,
						isSourceAudioEnabled: false,
						name: `${element.name} (freeze)`,
					},
					{
						...element,
						id: rightId,
						startTime: addMediaTime({
							a: this.freezeTime,
							b: this.freezeDuration,
						}),
						duration: rightVisibleDuration,
						trimStart: rightTrimStart,
						animations: rightAnimations,
					},
				];
			});

			return { ...track, elements } as TTrack;
		};

		const updatedTracks: SceneTracks = {
			overlay: this.savedState.overlay.map((track) => rebuildTrack(track)),
			main: rebuildTrack(this.savedState.main),
			audio: this.savedState.audio.map((track) => rebuildTrack(track)),
		};

		if (!this.freezeElement) {
			// Nothing was frozen (not a video, or freeze point outside the clip).
			this.savedState = null;
			return undefined;
		}

		editor.timeline.updateTracks(updatedTracks);
		return createElementSelectionResult([this.freezeElement]);
	}

	undo(): void {
		if (this.savedState) {
			const editor = EditorCore.getInstance();
			editor.timeline.updateTracks(this.savedState);
		}
	}
}
